import apiUrl from './api';
import { postWithAuth } from '@/lib/httpAdapter';

/** Repos the org has PR-review data for; populates the Code Review repo filter. */
export type CodeReviewReposRequest = {
  OrgUUID: string;
};

export type CodeReviewReposResponse = {
  repos: string[];
};

/**
 * Server order is preserved deliberately: the API returns repos busiest first so
 * the handful anyone actually filters by sit at the top. Re-sorting
 * alphabetically here would bury them among test and eval-harness repos.
 */
function parseRepos(json: unknown): string[] {
  if (!json || typeof json !== 'object') return [];
  const raw = (json as Record<string, unknown>).repos;
  if (!Array.isArray(raw)) return [];
  const names = raw
    .filter((r): r is string => typeof r === 'string')
    .map((r) => r.trim())
    .filter(Boolean);
  return Array.from(new Set(names));
}

/**
 * Fetch the repositories available to the Code Review repository filter.
 *
 * Returns only repos with PR-review data, so the selector never offers an option
 * that would render an all-zero dashboard.
 */
export async function fetchCodeReviewRepos(
  payload: CodeReviewReposRequest
): Promise<CodeReviewReposResponse> {
  const res = await postWithAuth(`${apiUrl}metrics/codeReviewRepos/`, {
    OrgUUID: payload.OrgUUID,
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = `Request failed (${res.status})`;
    try {
      const j = JSON.parse(text) as Record<string, unknown>;
      if (typeof j.error === 'string') msg = j.error;
    } catch {
      if (text) msg = text;
    }
    throw new Error(msg);
  }
  const json: unknown = await res.json();
  return { repos: parseRepos(json) };
}
